import type { FlipPad, PulseChart } from '@shared/chart';
import type { EngineState } from '@shared/engine/pulse/state';
import { PULSE_GOOD_WINDOW_MS } from '@shared/gameTypes';
import { drawPadShape } from './pulseDrawShapes';
import { hexToRgba } from './pulseRenderColour';

/**
 * Stateless drawers for the pad lane:
 *
 *   • drawPads — the actual coloured pad rectangles (+ optional/required
 *     halo). Self-culls anything off-screen via the viewWidth bound.
 *   • drawApproachRing — shrinking circle on top of the player that
 *     telegraphs the next required pad; osu!-style "press now" cue.
 *   • drawDownbeatTick — 1px halo flash on the player on every kick onset.
 *   • drawReceptorBand — persistent column at PLAYER_X with an optional
 *     "TAP HERE" label for first-run onboarding.
 */

export function drawPads(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  state: EngineState,
  beat: number,
  playerX: number,
  ceilingY: number,
  floorY: number,
  xPerBeat: number,
  padSize: number,
  viewWidth: number,
): void {
  // Half-width in playfield pixels of the good timing window. The pad
  // core renders at its existing fixed size so the visual doesn't shrink
  // on slow charts; the good-window half-width drives a flanking halo
  // that telegraphs "OK lands here" without moving the perfect zone.
  const goodHalfPx = (PULSE_GOOD_WINDOW_MS * chart.bpm) / 60_000 * xPerBeat;
  for (const pad of chart.pads) {
    if (pad.beat < beat - 0.5) {
      continue;
    }
    if (pad.beat > beat + viewWidth / xPerBeat + 1) {
      continue;
    }
    // Future pads sit to the RIGHT of playerX (add the offset);
    // pads scroll left toward the player.
    const padX = playerX + (pad.beat - beat) * xPerBeat;
    const padY = pad.surface === 'floor' ? floorY : ceilingY;
    const hit = state.hitPadIds.has(pad.id);
    drawPadShape(ctx, pad, padX, padY, padSize, hit, chart.palette, beat, goodHalfPx);
  }
}

/**
 * Shrinking circle drawn on top of the player that times the next required
 * pad. Starts at 2.5x size 1 beat out and contracts down to 1x as the pad
 * arrives — matches the player size at the perfect-window centre. Cribbed
 * from osu! approach circles; they're the most intuitive "press now"
 * indicator we know of.
 */
export function drawApproachRing(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  state: EngineState,
  beat: number,
  x: number,
  ceilingY: number,
  floorY: number,
  size: number,
): void {
  let nextPad: FlipPad | null = null;
  for (const pad of chart.pads) {
    if (pad.optional) {
      continue;
    }
    if (state.hitPadIds.has(pad.id)) {
      continue;
    }
    if (pad.beat < beat - 0.05) {
      continue;
    }
    if (pad.beat > beat + 1.05) {
      continue;
    }
    if (nextPad === null || pad.beat < nextPad.beat) {
      nextPad = pad;
    }
  }
  if (!nextPad) {
    return;
  }
  const distance = nextPad.beat - beat;
  if (distance < 0 || distance > 1) {
    return;
  }
  const t = distance;
  const radius = (size / 2) * (1 + t * 1.5);
  const alpha = Math.min(0.65, 0.2 + (1 - t) * 0.6);
  const surfaceY = nextPad.surface === 'floor' ? floorY - size / 2 : ceilingY + size / 2;
  ctx.save();
  ctx.globalAlpha = alpha;
  const ringColour =
    nextPad.surface === state.currentSurface ? chart.palette.padPerfect : chart.palette.pad;
  ctx.strokeStyle = ringColour;
  ctx.lineWidth = 2 + (1 - t) * 1.5;
  ctx.shadowColor = ringColour;
  ctx.shadowBlur = 8 + (1 - t) * 12;
  ctx.beginPath();
  ctx.arc(x, surfaceY, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * 1px halo flash on the player on every downbeat (kick onset). Reads
 * onsets from chart.audio so it's musically accurate even on syncopated
 * tracks where "downbeat" isn't every integer beat.
 */
export function drawDownbeatTick(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  beat: number,
  x: number,
  ceilingY: number,
  floorY: number,
  size: number,
): void {
  if (chart.audio.onsets.length === 0) {
    return;
  }
  let nearestKick: number | null = null;
  let nearestDist = Infinity;
  for (const onset of chart.audio.onsets) {
    if (onset.kind !== 'kick') {
      continue;
    }
    const d = Math.abs(onset.beat - beat);
    if (d < nearestDist) {
      nearestDist = d;
      nearestKick = onset.beat;
    }
    if (onset.beat > beat + 0.5) {
      break;
    }
  }
  if (nearestKick === null) {
    return;
  }
  const beatsToBeat = Math.abs(nearestKick - beat);
  if (beatsToBeat > 0.18) {
    return;
  }
  const intensity = 1 - beatsToBeat / 0.18;
  const surfaceY = floorY - size / 2;
  const ceilSurfaceY = ceilingY + size / 2;
  ctx.save();
  ctx.globalAlpha = intensity * 0.7;
  ctx.strokeStyle = chart.palette.ui;
  ctx.lineWidth = 1;
  ctx.shadowColor = chart.palette.ui;
  ctx.shadowBlur = 6 + intensity * 10;
  const ringR = size * (0.7 + intensity * 0.3);
  ctx.beginPath();
  ctx.arc(x, surfaceY, ringR, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, ceilSurfaceY, ringR, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * Persistent translucent strip at PLAYER_X spanning floor↔ceiling, plus a
 * "TAP HERE" label during first-run onboarding. Teaches the receptor
 * concept by making the column where pads have to be when you tap
 * unmissable. Always shown (low alpha) so experts barely register it; the
 * label only renders while the first-run hint is active.
 */
export function drawReceptorBand(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  x: number,
  ceilingY: number,
  floorY: number,
  size: number,
  beatPulse: number,
  showLabel: boolean,
): void {
  const bandHalfWidth = size * 0.55;
  const top = ceilingY;
  const bottom = floorY;
  ctx.save();

  const grad = ctx.createLinearGradient(x - bandHalfWidth, 0, x + bandHalfWidth, 0);
  grad.addColorStop(0, hexToRgba(chart.palette.pad, 0));
  grad.addColorStop(0.5, hexToRgba(chart.palette.pad, 0.16 + beatPulse * 0.08));
  grad.addColorStop(1, hexToRgba(chart.palette.pad, 0));
  ctx.fillStyle = grad;
  ctx.fillRect(x - bandHalfWidth, top, bandHalfWidth * 2, bottom - top);

  ctx.strokeStyle = hexToRgba(chart.palette.pad, 0.35 + beatPulse * 0.2);
  ctx.lineWidth = 1;
  ctx.shadowColor = chart.palette.pad;
  ctx.shadowBlur = 6 + beatPulse * 8;
  ctx.beginPath();
  ctx.moveTo(x, top);
  ctx.lineTo(x, bottom);
  ctx.stroke();

  if (showLabel) {
    const phase = (performance.now() / 600) % 1;
    const pulse = 0.55 + 0.45 * Math.sin(phase * Math.PI * 2);
    const labelY = (top + bottom) / 2;
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 0.45 + pulse * 0.4;
    ctx.fillStyle = chart.palette.pad;
    ctx.font = `bold 10px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.save();
    // Rotate +90deg so the label reads top-to-bottom (natural for
    // vertical text). Anchored a few px to the LEFT of the band — that
    // side is the past-pad lane (player on left, pads scroll right→left),
    // so the label sits in the small negative-space strip without
    // overlapping incoming pads on the right.
    ctx.translate(x - bandHalfWidth - 6, labelY);
    ctx.rotate(Math.PI / 2);
    ctx.fillText('TAP HERE', 0, 0);
    ctx.restore();
  }
  ctx.restore();
}
